import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, NavLink } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { getOrders } from 'redux/orders/actions';
import {
  Button,
  Checkbox,
  IconCheckmark,
  IconDisabled,
  List,
  Notification,
  Page,
  SearchForm,
} from 'shared/components';
import {
  displayValue,
  formatDate,
  getCurrentUserPermissions,
  getTableCustomerName,
  hasPermission,
  transformSort,
} from 'shared/helpers';
import { searchType } from 'shared/constants';
import { fetchMatchingOrders } from './actions';
import RemoveOrderUsersForm from 'orders/RemoveOrderUsersForm';
import UpdateOrderManagersForm from './UpdateOrderManagersForm';
import './styles.scss';

// update_managers accepts at most 100 orders per request
const MAX_SELECTED_ORDERS = 100;

const OrdersList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const orders = useSelector((state) => get(state, 'orders.list.results'));
  const loading = useSelector((state) => get(state, 'orders.loading'));
  const activeAccountId = useSelector((state) =>
    get(state, 'account.activeAccountId'),
  );
  const count = useSelector((state) => get(state, 'orders.list.count', 0));
  const pages = Math.ceil(count / 20);
  const permissionsList = useSelector((state) =>
    getCurrentUserPermissions(state),
  );
  const canManageManagers = hasPermission(permissionsList, 'manage_managers');
  const canManageLicenseUsers = hasPermission(
    permissionsList,
    'manage_license_users',
  );
  const canSelectOrders = canManageManagers || canManageLicenseUsers;

  const [page, setPage] = useState(0);
  // The search type only applies once the search is submitted, so switching
  // it doesn't rerun the previous text as another kind of search
  const [searchBy, setSearchBy] = useState(searchType.order_storeID);
  const [activeSearch, setActiveSearch] = useState({
    query: '',
    type: searchType.order_storeID,
  });
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  // Kept across pages and searches, so a manager's orders can be collected
  // from several result pages before updating them together
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isUpdateFormDisplayed, setUpdateFormDisplay] = useState(false);
  const [isRemoveUsersFormDisplayed, setRemoveUsersFormDisplay] =
    useState(false);
  const [isSelectingAll, setSelectingAll] = useState(false);
  // The search whose orders were all selected, so the offer isn't repeated for it
  const [allFoundSearch, setAllFoundSearch] = useState(null);
  // Bumped after a bulk change, so the list shows the orders as they are now
  const [listVersion, setListVersion] = useState(0);

  const accountFilter = activeAccountId
    ? `&customer__account=${activeAccountId}`
    : '';

  useEffect(() => {
    setPage(0);
    setSelectedOrders([]);
  }, [activeAccountId]);

  useEffect(() => {
    if (!selectedOrders.length) {
      setAllFoundSearch(null);
    }
  }, [selectedOrders.length]);

  useEffect(() => {
    dispatch(
      getOrders(
        page,
        activeSearch.query,
        activeSearch.type,
        transformSort(currentSort),
        accountFilter,
      ),
    );
  }, [page, activeSearch, currentSort, dispatch, accountFilter, listVersion]);

  const redirectToOrderPage = (rowData) => {
    const orderId = get(rowData, 'original.id');
    history.push(`/orders/${orderId}`);
  };

  const handleOrdersChanged = (notProcessedOrders = []) => {
    setSelectedOrders(notProcessedOrders);
    setListVersion((version) => version + 1);
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handleSortChange = (newSorted) => setCurrentSort(newSorted);
  const handleClear = () => {
    setActiveSearch({ query: '', type: searchBy });
    setPage(0);
  };

  const handleSearchSubmit = (val) => {
    setActiveSearch({ query: val, type: searchBy });
    setPage(0);
  };

  const isSelected = (order) =>
    selectedOrders.some((selected) => selected.id === order.id);
  const isSelectionFull = selectedOrders.length >= MAX_SELECTED_ORDERS;
  const pageOrders = orders || [];
  const isPageSelected =
    pageOrders.length > 0 && pageOrders.every((order) => isSelected(order));

  const toSelection = (order) => ({ id: order.id, store_id: order.store_id });
  const hasMoreThanPage = count > pageOrders.length;
  const canSelectAllFound =
    isPageSelected &&
    hasMoreThanPage &&
    !isSelectionFull &&
    allFoundSearch !== activeSearch;

  const selectAllFound = () => {
    setSelectingAll(true);
    fetchMatchingOrders(
      activeSearch.query,
      activeSearch.type,
      transformSort(currentSort),
      accountFilter,
      MAX_SELECTED_ORDERS,
    )
      .then((res) => {
        setAllFoundSearch(activeSearch);
        setSelectedOrders((selected) => {
          const toAdd = get(res, 'data.results', [])
            .filter((order) => !selected.some((s) => s.id === order.id))
            .map(toSelection);
          return selected.concat(toAdd).slice(0, MAX_SELECTED_ORDERS);
        });
      })
      .catch(() =>
        Notification(
          'error',
          t('Error occured'),
          t('The orders could not be selected'),
        ),
      )
      .finally(() => setSelectingAll(false));
  };

  const toggleOrder = (order) =>
    setSelectedOrders((selected) => {
      if (selected.some((s) => s.id === order.id)) {
        return selected.filter((s) => s.id !== order.id);
      }
      if (selected.length >= MAX_SELECTED_ORDERS) {
        return selected;
      }
      return selected.concat(toSelection(order));
    });

  const togglePage = () =>
    setSelectedOrders((selected) => {
      if (isPageSelected) {
        return selected.filter(
          (s) => !pageOrders.some((order) => order.id === s.id),
        );
      }
      const toAdd = pageOrders
        .filter((order) => !selected.some((s) => s.id === order.id))
        .map(toSelection);
      return selected.concat(toAdd).slice(0, MAX_SELECTED_ORDERS);
    });

  const searchOptions = [
    {
      label: t('Search by order id'),
      value: searchType.order_storeID,
    },
  ].concat(
    canManageManagers
      ? [
          {
            label: t('Search by manager email'),
            value: searchType.managerEmail,
          },
        ]
      : [],
  );

  const selectColumn = {
    id: 'select',
    className: 'select-container',
    Header: () => (
      <button
        className="table-select"
        aria-label="Select orders on this page"
        onClick={(e) => {
          e.stopPropagation();
          togglePage();
        }}
        type="button"
      >
        <Checkbox inputId="orders-select-page" checked={isPageSelected} />
      </button>
    ),
    Cell: (rowData) => {
      const selected = isSelected(rowData.original);
      return (
        <button
          className="table-select"
          aria-label="Select order"
          disabled={!selected && isSelectionFull}
          onClick={(e) => {
            e.stopPropagation();
            toggleOrder(rowData.original);
          }}
          type="button"
        >
          <Checkbox
            inputId={`${rowData.original.id}-select`}
            checked={selected}
            disabled={!selected && isSelectionFull}
          />
        </button>
      );
    },
    width: 60,
    sortable: false,
  };

  return (
    <Page
      title={t('Orders')}
      breadcrumb={<NavLink to="/">{t('Home')}</NavLink>}
    >
      <div className="OrdersList">
        <div className="list-header">
          <SearchForm
            options={searchOptions}
            selectedValue={searchBy}
            handleSearchTypeChange={(newSearchType) =>
              setSearchBy(newSearchType)
            }
            handleSearchSubmit={handleSearchSubmit}
            activeQuery={activeSearch.query}
            handleClear={handleClear}
            disabled={loading}
          />
          <div />
        </div>
        {canSelectOrders && selectedOrders.length > 0 && (
          <div className="OrdersList-selection">
            <div className="OrdersList-selection-info">
              <strong>
                {`${t('Selected orders')}: ${selectedOrders.length}`}
              </strong>
              {isSelectionFull && (
                <span>{t('This is the maximum for one update.')}</span>
              )}
              {canSelectAllFound && (
                <Button
                  theme="link"
                  size="sm"
                  onClick={selectAllFound}
                  disabled={isSelectingAll}
                >
                  {count > MAX_SELECTED_ORDERS
                    ? t('Select the first 100 orders found')
                    : `${t('Select all orders found')} (${count})`}
                </Button>
              )}
            </div>
            <div className="OrdersList-selection-actions">
              <Button
                theme="link"
                size="sm"
                onClick={() => setSelectedOrders([])}
              >
                {t('Clear selection')}
              </Button>
              {canManageLicenseUsers && (
                <Button
                  theme="info"
                  size="sm"
                  onClick={() => setRemoveUsersFormDisplay(true)}
                >
                  {t('Remove users')}
                </Button>
              )}
              {canManageManagers && (
                <Button
                  theme="info"
                  size="sm"
                  onClick={() => setUpdateFormDisplay(true)}
                >
                  {t('Update managers')}
                </Button>
              )}
            </div>
          </div>
        )}
        <List
          data={orders || []}
          columns={[
            ...(canSelectOrders ? [selectColumn] : []),
            {
              accessor: 'created_at',
              Header: t('Created on'),
              Cell: (cellInfo) => formatDate(cellInfo.value),
              width: 180,
            },
            {
              accessor: 'store_id',
              Header: t('Order ID'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'customer.email',
              Header: t('Customer email'),
              Cell: (cellData) => displayValue(cellData.value),
              maxWidth: 400,
            },
            {
              accessor: 'customer.last_name',
              Header: t('Customer name'),
              Cell: (rowData) => getTableCustomerName(rowData.original),
            },
            {
              accessor: 'customer.company_name',
              Header: t('Customer company'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'updated_at',
              Header: t('Last updated'),
              Cell: (cellInfo) => formatDate(cellInfo.value),
            },
            {
              accessor: 'type',
              Header: t('Type'),
              Cell: (cellData) => displayValue(cellData.value),
            },
            {
              accessor: 'is_trial',
              Header: t('Is trial'),
              headerClassName: 'text-center',
              className: 'text-center',
              Cell: (cellData) =>
                cellData.value ? (
                  <IconCheckmark color="#10ac84" height="14px" />
                ) : (
                  <IconDisabled color="#aaa" height="14px" />
                ),
              width: 80,
            },
          ]}
          clickable
          page={page}
          pages={pages}
          loading={loading}
          manual
          minRows={get(orders, 'length', 10)}
          showPagination={pages > 1}
          onPageChange={handlePageChange}
          onSortedChange={handleSortChange}
          defaultSorted={currentSort}
          handleClick={(rowData) => redirectToOrderPage(rowData)}
        />
      </div>
      {isRemoveUsersFormDisplayed && (
        <RemoveOrderUsersForm
          orders={selectedOrders}
          closeCb={() => setRemoveUsersFormDisplay(false)}
          confirmCb={handleOrdersChanged}
        />
      )}
      {isUpdateFormDisplayed && (
        <UpdateOrderManagersForm
          orders={selectedOrders}
          closeCb={() => setUpdateFormDisplay(false)}
          confirmCb={handleOrdersChanged}
        />
      )}
    </Page>
  );
};

export default OrdersList;
